import React, { useRef, useState, useEffect } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import styled, { ThemeProvider } from "styled-components";
import imgNotSupported from "../icons/ImageNotSupportedIcon/image-not-supported.svg";
import SwiperCore, { Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import theme from "../../style/theme";
import ButtonBase from "../button/ButtonBase";
import RefreshLineIcon from "../icons/RefreshLineIcon/RefreshLineIcon";
import { CustomNavigation } from "../navigation/CustomNavigation";

interface ImageActionKeysMap {
    [imageUrl: string]: Set<string>;
}

interface CustomSwiperProps {
    children?: any;
    onClick?: (e: any) => void;
    onClickLeft?: (e: any) => void;
    onClickRight?: (e: any) => void;
    name?: string;
    prevRef?: React.Ref<HTMLButtonElement>;
    nextRef?: React.Ref<HTMLButtonElement>;
    ref?: React.Ref<HTMLButtonElement>;
    prevClassName?: string;
    nextClassName?: string;
    imgItemsContents?: Array<string>;
    sx?: object | undefined;
    imageActionKeysMap?: ImageActionKeysMap;
}

export const CustomSwiper: React.FC<CustomSwiperProps> = (
    {
        sx,
        imgItemsContents,
        imageActionKeysMap,
    }
) => {
    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [imgRowColumn, setImgRowColumn] = useState(true);
    const swiperRef = useRef<any>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                swiperRef.current?.swiper?.slidePrev();
            } else if (e.key === 'ArrowRight') {
                swiperRef.current?.swiper?.slideNext();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const resetRotation = () => {
        setRotation(0);
    };

    const rotateRight = () => {
        if (rotation === 3) {
            setRotation(0);
        } else {
            setRotation(rotation + 1);
        }

        rotation % 2 == 0 ? setImgRowColumn(false) : setImgRowColumn(true);
    };

    //swiper library
    SwiperCore.use([Navigation, Pagination]);

    const transformRef = useRef<any>(null);

    //imgItemsArray로 복사하기
    const imgItemsArray = imgItemsContents && [...imgItemsContents];

    const imgItemsList = imgItemsArray?.map((item: any, idx: any) => (
        <SwiperSlide style={{ height: "100%", width: "100%" }} key={idx}>
            <div
                style={{
                    width: "501px",
                    height: "668px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative"
                }}
            >
                <img
                    className={`${imgRowColumn ? "column" : "row"}`}
                    src={`${item}`}
                    alt="주소"
                    style={{ transform: `rotate(${rotation * 90}deg)` }}
                />
                {imageActionKeysMap && imageActionKeysMap[item] && (
                    <ImageOverlay>
                        {[...imageActionKeysMap[item]].map((actionKey, index) => (
                            <ActionKeyTag key={index}>
                                {actionKey}
                            </ActionKeyTag>
                        ))}
                    </ImageOverlay>
                )}
            </div>
        </SwiperSlide>
    ));

    //zoom, pan, pitch//transform wrap box
    const transformWrapperBox = (
        <TransformWrapper
            ref={transformRef}
            panning={{ disabled: scale <= 1, velocityDisabled: true }}
            onZoom={(ref) => setScale(ref.state.scale)}
        >
            {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                <CustomDiv
                    style={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        boxSizing: "border-box",
                        background: "#1C2026",
                        borderBottomLeftRadius: "8px",
                    }}
                >
                    <CustomNavigationBox
                        prevClassName="prev"
                        nextClassName="next"
                        onClickLeft={(e) => {
                            resetTransform();
                            resetRotation();
                        }}
                        onClickRight={(e) => {
                            resetTransform();
                            resetRotation();
                        }}
                    />
                    <Toolbox>
                        <ToolButton
                            onClick={(e: any) => {
                                zoomIn();
                                setScale(transformRef?.current?.state.scale);
                            }}
                        >
                            +
                        </ToolButton>
                        <ToolButton
                            onClick={(e: any) => {
                                zoomOut();
                                setScale(transformRef?.current?.state.scale);
                            }}
                        >
                            -
                        </ToolButton>
                        <ToolButton
                            onClick={(e: any) => {
                                rotateRight();
                                setScale(transformRef?.current?.state.scale);
                            }}
                        >
                            <RefreshLineIcon />
                        </ToolButton>
                    </Toolbox>
                    <div
                        style={{
                            color: "black",
                            background: `${theme.palette.primitives.gray200}`,
                            bottom: "0px",
                        }}
                        className="swiper-pagination"
                    ></div>

                    <TransformComponent>
                        <Swiper
                            ref={swiperRef}
                            cssMode={true}
                            navigation={{
                                prevEl: ".prev",
                                nextEl: ".next",
                            }}
                            style={{
                                textAlign: "center",
                                height: "100%",
                                borderBottomLeftRadius: "8px",
                            }}
                            pagination={{
                                el: ".swiper-pagination",
                                type: "fraction",
                            }}
                            className="mySwiper"
                        >
                            {imgItemsList}
                        </Swiper>
                    </TransformComponent>
                </CustomDiv>
            )}
        </TransformWrapper>
    );

    const CustomEmptyBoxContainer = (
        <CustomEmptyBox style={sx}>
            <CustomEmptyImg></CustomEmptyImg>
            <CustomEmptyMain>아직 물품 사진이 없습니다.</CustomEmptyMain>
            <CustomEmptySub>
                담당 프로님이 촬영을 완료하면 이곳에 사진이 등록됩니다.
            </CustomEmptySub>
        </CustomEmptyBox>
    );

    return (
        <React.Fragment>
            <ThemeProvider theme={theme}>
                <CustomContainer style={sx}>
                    {imgItemsContents && imgItemsContents.length > 0
                        ? transformWrapperBox
                        : CustomEmptyBoxContainer}
                </CustomContainer>
            </ThemeProvider>
        </React.Fragment>
    );
};

const CustomContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const CustomEmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #ebeef3;
`;

const CustomEmptyImg = styled.div`
  width: 104px;
  height: 104px;
  margin: 0 auto;
  background: url(${imgNotSupported});
  margin-bottom: 16px;
`;

const CustomEmptyMain = styled.p`
  margin: 0;
  font-weight: 800;
  font-size: 24px;
  line-height: 28px;
  color: ${(props: any) => props.theme.palette.primitives.gray400};
  margin-bottom: 8px;
  text-align: center;
`;

const CustomEmptySub = styled.p`
  margin: 0;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${(props: any) => props.theme.palette.primitives.gray400};
  text-align: center;
`;

const CustomNavigationBox = styled(CustomNavigation)`
  position: absolute;
  box-sizing: border-box;
`;

const CustomDiv = styled.div`
  & .swiper-pagination-fraction {
    display: flex;
    align-items: center;
    justify-content: space-around;
    min-width: 35px;
    min-height: 35px;
    width: auto;
    height: auto;
    padding: 5px 8px;
    box-sizing: border-box;
    color: #ffffff;
    bottom: 26px;
    left: 90%;
    transform: translate(-50%, -50%);
    font-size: 14px;
    border-radius: 4px;
  }

  & .column {
    display: block;
    max-width: 501px;
    max-height: 488px;
  }

  & .row {
    display: block;
    max-width: 100%;
    max-height: 501px;
  }

  & .swiper-slide-active img {
    transform: rotate(${(props: any) => props.rotation * 90}deg);
  }

  & .react-transform-wrapper,
  & .react-transform-component {
    margin: 0 auto;
    height: 100%;
  }

  & .react-transform-component {
    width: 100%;
  }

  & .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Toolbox = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  z-index: 2000;
`;

const ToolButton = styled(ButtonBase)`
  background: ${theme.palette.primitives.gray200};
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 15px;
  margin-left: 10px;

  & path {
    fill: black;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 54px;
  left: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ActionKeyTag = styled.span`
  background: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(4px);
`;
